import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Alert, Button } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { usePhase } from '../../../contexts/PhaseContext';
import { fetchJson } from '../../../utils/fetchJson';
import MultiStudentAdmissionForm from '../../../components/School_Admin/StudentManagement/MultiStudentAdmissionForm';
import StudentImportDialog from '../../../components/School_Admin/StudentManagement/StudentImportDialog';

const AdmissionsPage = () => {
  const { t } = useTranslation();
  const [classes, setClasses] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [importedStudents, setImportedStudents] = useState([]);
  const { activePhase } = usePhase();

  useEffect(() => {
    let active = true;
    const normalize = (arr) => (Array.isArray(arr) ? arr.map(c => ({ id: c.id, name: c.name || c.className || `Class ${c.id}` })) : []);

    (async () => {
      try {
        const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
        const headers = schoolId ? { 'X-Scope-School-Id': String(schoolId) } : {};
        const qPhase = activePhase ? `?phase=${encodeURIComponent(activePhase)}` : '';
        const res = await fetchJson(`/api/classes-lite${qPhase}`, { headers });
        const list = Array.isArray(res?.classes) ? res.classes : [];
        if (active) setClasses(normalize(list));
      } catch (e) {
        if (active) {
          setClasses([]);
          setLoadError(t('admissions.loadClassesError'));
        }
      }
    })();
    return () => { active = false; };
  }, [activePhase]);

  const handleImportEnhanced = (studentsArr) => {
    if (studentsArr && typeof studentsArr === 'object' && Array.isArray(studentsArr.students)) {
      setImportedStudents([]);
      setShowImport(false);
      return;
    }

    if (!Array.isArray(studentsArr) || studentsArr.length === 0) {
      setImportedStudents([]);
      setShowImport(false);
      return;
    }

    const mapped = studentsArr.map((student) => {
      let classId = student?.classId || '';
      if (!classId && student?.className && Array.isArray(classes)) {
        const found = classes.find(
          (cls) => (cls?.name || '').trim().toLowerCase() === (student.className || '').trim().toLowerCase()
        );
        if (found) classId = found.id;
      }
      return {
        ...student,
        classId
      };
    });

    setImportedStudents(mapped);
    setShowImport(false);
  };

  return (
    <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, boxShadow: 3, p: { xs: 2, md: 4 }, mt: 2 }}>
      {(classes?.length === 0 || loadError) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {loadError || t('admissions.noClassesWarning')}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => setShowImport(true)}
          startIcon={<CloudUploadIcon />}
          sx={{ borderRadius: 2, px: 3, py: 1.5, fontWeight: 600 }}
        >
          {t('admissions.importStudents')}
        </Button>
      </Box>

      <MultiStudentAdmissionForm classes={classes} initialStudents={importedStudents} />

      <StudentImportDialog
        open={showImport}
        onClose={() => setShowImport(false)}
        onImport={handleImportEnhanced}
        fullWidth
        maxWidth="lg"
      />
    </Box>
  );
};

export default AdmissionsPage;
